import React, { useState, useEffect } from "react";
import { useCredit } from "../context/CreditContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { WithdrawPanel } from "./WithdrawModal";
import { Plus, Banknote } from "lucide-react";

// Unified wallet modal: top-level Deposit / Withdraw tabs.
export default function WalletModal({ open, onOpenChange, initialTab = "deposit", initialPromo = null }) {
  const [tab, setTab] = useState(initialTab);
  useEffect(() => { if (open) setTab(initialTab); }, [open, initialTab]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#19142A] dark:bg-[#141021] border border-[#2A2440] dark:border-[#2A2440] rounded-lg max-w-md text-white dark:text-white">
        <DialogHeader><DialogTitle className="font-cases uppercase">Wallet</DialogTitle></DialogHeader>
        <div className="flex gap-2 -mt-1 mb-2">
          <button data-testid="wallet-tab-deposit" onClick={() => setTab("deposit")}
            className={`flex-1 py-2 text-[11px] font-bold uppercase border flex items-center justify-center gap-1 ${tab === "deposit" ? "bg-[#7C4DFF] dark:bg-[#9945FF] text-white border-transparent" : "border-[#2A2440] dark:border-[#2A2440] text-[#8A90A0]"}`}>
            <Plus className="h-3.5 w-3.5" />Deposit
          </button>
          <button data-testid="wallet-tab-withdraw" onClick={() => setTab("withdraw")}
            className={`flex-1 py-2 text-[11px] font-bold uppercase border flex items-center justify-center gap-1 ${tab === "withdraw" ? "bg-[#7C4DFF] dark:bg-[#9945FF] text-white border-transparent" : "border-[#2A2440] dark:border-[#2A2440] text-[#8A90A0]"}`}>
            <Banknote className="h-3.5 w-3.5" />Withdraw
          </button>
        </div>
        {tab === "deposit" && <DepositPanel onDone={() => onOpenChange(false)} initialPromo={open ? initialPromo : null} />}
        {tab === "withdraw" && <WithdrawPanel onDone={() => onOpenChange(false)} />}
      </DialogContent>
    </Dialog>
  );
}

function DepositPanel({ onDone, initialPromo = null }) {
  const { startDeposit, validatePromo } = useCredit();
  const [amount, setAmount] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [promoInput, setPromoInput] = useState("");
  const [applied, setApplied] = useState(null);
  const [promoErr, setPromoErr] = useState("");
  const [checkingPromo, setCheckingPromo] = useState(false);

  // Auto-fill + apply a promo (e.g. from the Deposit banner).
  useEffect(() => {
    if (!initialPromo) return;
    setPromoInput(initialPromo);
    setAmount("25");
    (async () => {
      try {
        const res = await validatePromo(initialPromo, 25);
        if (res.valid) setApplied({ code: res.code, pct: res.pct, max_deposit: res.max_deposit });
      } catch (e) {}
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialPromo]);

  const amt = parseFloat(amount);
  const valid = !isNaN(amt) && amt >= 5;
  const bonus = applied && valid ? Math.round(amt * applied.pct) / 100 : 0;
  const total = valid ? Math.round((amt + bonus) * 100) / 100 : 0;

  const applyPromo = async () => {
    setPromoErr("");
    const code = promoInput.trim();
    if (!code) return;
    if (!valid) { setPromoErr("Enter a deposit amount ($5+) first"); return; }
    setCheckingPromo(true);
    try {
      const res = await validatePromo(code, amt);
      if (res.valid) { setApplied({ code: res.code, pct: res.pct, max_deposit: res.max_deposit }); }
      else { setApplied(null); setPromoErr(res.message || "Invalid promo code"); }
    } catch (e) {
      setApplied(null);
      setPromoErr(e?.response?.data?.detail || "Could not check code");
    }
    setCheckingPromo(false);
  };

  const go = async () => {
    setErr("");
    if (!valid) { setErr("Minimum deposit is $5"); return; }
    setLoading(true);
    try {
      const url = await startDeposit(Math.round(amt * 100) / 100, applied?.code);
      window.location.href = url;
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not start checkout. Try again.");
      setLoading(false);
    }
  };

  return (
    <div data-testid="deposit-panel">
      <p className="text-xs text-[#8A90A0]">Pay securely with card via Stripe. Your credit is added instantly after payment. Minimum $5.</p>
      <div className="mt-3">
        <label className="text-xs font-semibold uppercase text-[#8A90A0]">Amount (USD)</label>
        <div className={`mt-1 flex items-center border border-[#2A2440] dark:border-[#2A2440] bg-[#19142A] dark:bg-[#1E1832] ${applied ? "opacity-60" : ""}`}>
          <span className="pl-3 pr-1 text-lg font-bold text-[#8A90A0]">$</span>
          <input
            data-testid="deposit-amount-input"
            type="number" min="5" step="1" inputMode="decimal"
            value={amount}
            disabled={!!applied}
            onChange={(e) => { setAmount(e.target.value); setErr(""); }}
            onKeyDown={(e) => { if (e.key === "Enter" && valid && !loading) go(); }}
            placeholder="25"
            className="w-full bg-transparent py-2.5 pr-3 text-lg font-bold font-grotesk outline-none text-white dark:text-white placeholder:text-[#B7BECC] disabled:cursor-not-allowed"
            autoFocus
          />
        </div>
        {applied && <p className="text-[10px] text-[#8A90A0] mt-1">Remove the promo code to change your amount.</p>}
        <div className="mt-2 flex gap-2">
          {[10, 25, 50, 100].map((p) => (
            <button key={p} data-testid={`deposit-preset-${p}`} disabled={!!applied} onClick={() => { setAmount(String(p)); setErr(""); }}
              className="flex-1 py-1.5 text-xs font-bold border border-[#2A2440] dark:border-[#2A2440] hover:bg-[#19142A] dark:hover:bg-[#2A2440] disabled:opacity-50 disabled:cursor-not-allowed">
              ${p}
            </button>
          ))}
        </div>

        <label className="text-xs font-semibold uppercase text-[#8A90A0] block mt-4">Promo code</label>
        <div className="mt-1 flex gap-2">
          <input
            data-testid="promo-input"
            value={promoInput}
            onChange={(e) => { setPromoInput(e.target.value.toUpperCase()); setPromoErr(""); }}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); applyPromo(); } }}
            placeholder="Promo code"
            disabled={!!applied}
            className="w-full border border-[#2A2440] dark:border-[#2A2440] bg-[#19142A] dark:bg-[#1E1832] py-2 px-3 text-sm font-bold font-grotesk tracking-wide outline-none text-white dark:text-white placeholder:text-[#B7BECC] disabled:opacity-60"
          />
          {applied ? (
            <button data-testid="promo-remove-btn" onClick={() => { setApplied(null); setPromoInput(""); setPromoErr(""); }}
              className="px-3 text-xs font-bold border border-[#2A2440] dark:border-[#2A2440] hover:bg-[#19142A] dark:hover:bg-[#2A2440]">
              Remove
            </button>
          ) : (
            <button data-testid="promo-apply-btn" onClick={applyPromo} disabled={!promoInput.trim() || checkingPromo}
              className="px-3 text-xs font-bold bg-[#7C4DFF] dark:bg-[#9945FF] text-white disabled:opacity-50">
              {checkingPromo ? "…" : "Apply"}
            </button>
          )}
        </div>
        {promoErr && <p data-testid="promo-error" className="text-xs text-[#DB2777] mt-1">{promoErr}</p>}
        {applied && (
          <div data-testid="promo-applied" className="mt-2 border border-[#14B87A]/40 bg-[#14B87A]/10 px-3 py-2 text-xs">
            <span className="font-bold text-[#0E9f6e] dark:text-[#3ddc97]">{applied.code} applied</span>
            <span className="text-[#8A90A0]"> · {applied.pct}% bonus on deposits up to ${applied.max_deposit} (one-time)</span>
            {valid && <div className="mt-1 font-grotesk font-bold text-white dark:text-white">+${bonus.toFixed(2)} bonus → you get ${total.toFixed(2)}</div>}
          </div>
        )}

        {err && <p data-testid="deposit-error" className="text-xs text-[#DB2777] mt-2">{err}</p>}
        <Button data-testid="deposit-submit-btn" disabled={!valid || loading} onClick={go}
          className="w-full h-11 mt-3 rounded-lg font-grotesk font-bold bg-[#7C4DFF] dark:bg-[#9945FF] hover:bg-[#6A3AE6] dark:hover:bg-[#8236e6] text-white disabled:opacity-50">
          {loading ? "Redirecting to Stripe…" : `Continue to payment${valid ? ` · $${amt.toFixed(2)}` : ""}`}
        </Button>
        <p className="text-[10px] text-[#8A90A0] mt-2 text-center">Test mode · use card 4242 4242 4242 4242, any future date & CVC.</p>
      </div>
    </div>
  );
}
